import React from "react";
import PropTypes from "prop-types";
import { Card } from "@opendate-app/setlist";
import MembershipProductCardView, { membershipBenefitSummary } from "../../ConfirmTicketing/components/MembershipProductCardView";
import { blankMembershipDiscount } from "../scheduleBuilder";

const money = (value) => (value === "" || value == null ? "" : `$${Number(value).toFixed(2)}`);
const wholeNumber = (value) => Math.max(0, parseInt(value, 10) || 0);

// The confirm's membership card, holding the series' plan instead of saving
// to the server: each date gets these settings when it's built.
const MembershipCard = ({ product, membership, ticketTypeNames, addOnNames, onChange }) => {
  const enabled = membership.enabled == null ? product.defaultEnabled : membership.enabled;
  const discount = membership.discount;
  const patch = (changes) => onChange({ enabled, ...membership, ...changes });
  const patchDiscount = (changes) => patch({ discount: { ...discount, ...changes } });
  const limit = discount ? wholeNumber(discount.ticketLimitPerMember) : 0;
  const setLimit = (value) => patchDiscount({ ticketLimitPerMember: value > 0 ? String(value) : "" });

  return (
    <MembershipProductCardView
      idKey={product.id}
      name={product.name}
      editUrl={product.editUrl}
      priceLabel={money(product.price)}
      description={product.description}
      imageUrl={product.imageThumbUrl}
      sellChecked={Boolean(enabled)}
      onSellToggle={() => patch({ enabled: !enabled })}
      showAnnual
      annualPrice={membership.price || ""}
      onAnnualPriceChange={(event) => patch({ price: event.target.value.replace(/[^0-9.]/g, "") })}
      showMonthly={Boolean(product.monthlyPrice)}
      monthlyPrice={membership.monthlyPrice || ""}
      onMonthlyPriceChange={(event) => patch({ monthlyPrice: event.target.value.replace(/[^0-9.]/g, "") })}
      priceHint="Modifying this price will only apply to memberships purchased for these shows. Leave it blank to use the membership's own price."
      benefitsEnabled={Boolean(discount)}
      onBenefitsToggle={() => patch({ discount: discount ? null : blankMembershipDiscount() })}
      discountType={discount ? discount.discountType : "percentage"}
      onDiscountTypeChange={(discountType) => patchDiscount({ discountType, discount: "0" })}
      percentage={discount ? discount.discount : ""}
      onPercentageChange={(event) =>
        patchDiscount({ discount: event.target.value === "" ? "" : String(Math.min(100, wholeNumber(event.target.value))) })
      }
      amount={discount ? discount.discount : ""}
      onAmountChange={(event) => patchDiscount({ discount: event.target.value.replace(/[^0-9.]/g, "") })}
      limit={limit}
      onLimitChange={(event) => setLimit(wholeNumber(event.target.value))}
      onLimitStep={(delta) => setLimit(Math.max(0, limit + delta))}
      household={Boolean(product.household)}
      appliesToTickets={Boolean(discount && discount.appliesToTicketTypes)}
      onAppliesToTicketsChange={(checked) => patchDiscount({ appliesToTicketTypes: checked, revealedTicketTypeNames: [] })}
      ticketOptions={ticketTypeNames.map((name) => ({ value: name, label: name }))}
      selectedTicketIds={discount ? discount.revealedTicketTypeNames : []}
      onTicketsChange={(names) => patchDiscount({ revealedTicketTypeNames: names || [] })}
      appliesToAddOns={Boolean(discount && discount.appliesToAddOns)}
      onAppliesToAddOnsChange={(checked) => patchDiscount({ appliesToAddOns: checked, revealedAddOnNames: [] })}
      addOnOptions={addOnNames.map((name) => ({ value: name, label: name }))}
      selectedAddOnIds={discount ? discount.revealedAddOnNames : []}
      onAddOnsChange={(names) => patchDiscount({ revealedAddOnNames: names || [] })}
      summary={
        discount
          ? membershipBenefitSummary({
            name: product.name,
            discountType: discount.discountType,
            percentage: discount.discount,
            amount: discount.discount,
            appliesToTickets: discount.appliesToTicketTypes,
            ticketNames: discount.revealedTicketTypeNames || [],
            appliesToAddOns: discount.appliesToAddOns,
            addOnNames: discount.revealedAddOnNames || [],
            limit,
            household: product.household,
          })
          : null
      }
    />
  );
};

MembershipCard.propTypes = {
  product: PropTypes.object.isRequired,
  membership: PropTypes.object.isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  addOnNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

const MembershipsTab = ({ products, memberships, ticketTypeNames, addOnNames, onChange }) => {
  if (products.length === 0) {
    return (
      <div className="series-tab-panel">
        <h3 style={{ fontSize: "18px" }}>
          <strong>Memberships</strong>
        </h3>
        <p className="series-edit-hint">
          No membership products are available for this venue. Create membership products in your team settings to
          offer them during checkout.
        </p>
      </div>
    );
  }

  return (
    <div className="series-tab-panel">
      <Card>
        <h3 style={{ fontSize: "18px" }} className="mb-3">
          <strong>Memberships</strong>
        </h3>
        <p className="small series-edit-hint">Configure membership checkout and discount settings for every date in this show type.</p>
        {products.map((product) => (
          <MembershipCard
            key={product.id}
            product={product}
            membership={memberships[product.id] || {}}
            ticketTypeNames={ticketTypeNames}
            addOnNames={addOnNames}
            onChange={(membership) => onChange({ ...memberships, [product.id]: membership })}
          />
        ))}
      </Card>
    </div>
  );
};

MembershipsTab.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  memberships: PropTypes.object.isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  addOnNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default MembershipsTab;
